import { Component, OnDestroy, Inject, HostListener, ElementRef, ViewChild, ViewChildren, QueryList, Input, Output, EventEmitter } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import {
    FormGroup,
    FormControl,
    FormArray,
    AbstractControl,
    ValidationErrors,
    Validators,
    ValidatorFn
} from '@angular/forms';

import { Subscription, filter, combineLatest, debounceTime, finalize, tap, BehaviorSubject, of, Observable, switchMap, map } from 'rxjs';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { Moment } from 'moment';

import { SearchContextsEnum } from '../../enums/search-contexts.enum';
import { MonitoringKeywordGroupModel } from '../../models/monitoring-keyword-group.model';
import { MentionSearchModel } from '../../models/mention-search.model';
import { MentionSearchQueryModel } from '../../models/mention-search-query.model';
import { SentimentModel } from '../../models/sentiment.model';
import { LanguageModel } from '../../../../Core/Localization/models/language.model';
import { JournodexTypeModel } from '../../../../Common/Journodex/models/journodex-type.model';
import { JournodexSourceModel } from '../../../../Common/Journodex/models/journodex-source.model';
import { MonitoringRouteModel } from '../../models/monitoring-route.model';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { JournodexSourceTypeEnum } from '../../enums/journodex-source-type.enum';
import { MediaTypeModel } from '../../models/media-type.model';
import { RouteType } from '../../enums/route-type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { getMonitoringDateQueryInputTopicOptions, getMonitoringDateQueryInputFolderOptions } from '../../models/monitoring-date-query-input-options';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { SaveTopicDialogComponent } from '../SaveTopicDialog/save-topic-dialog.component';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { MentionSearchListItemModel } from '../../models/mention-search-list-item.model';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { SearchResultsService } from '../../../Core/services/search-results.service';
import { SearchRangeModel } from '../../../Core/models/search-range.model';
import { MonitoringSearchRegionSelectDialogService } from '../../services/monitoring-search-region-select-dialog.service';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { MonitoringSearchLanguageSelectDialogService } from '../../services/monitoring-search-language-select-dialog.service';
import { TabsStyleTypesEnum } from '../../../../Common/Layout/enums/tabs-types.enum';
import { DataSelectDialogModel } from '../../../../Common/Dialogs/models/data-select-dialog.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { ManageSourcesDialogComponent } from '../../../../Common/Sources/components/ManageSourcesDialog/manage-sources-dialog.component';
import { FolderService } from '../../../Core/services/folder.service';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { SeriesTypeEnum } from '../../../../Common/Reports/enums/series-type.enum';
import { MultiselectAutocompleteModel } from '../../../../Common/Forms/models/multiselect-autocomplete.model';
import { SearchCriteriaSourcesEnum, SearchCriteriaSourcesEnum as SourceTypeEnum } from '../../../../api/mentions/enum/search-criteria-sources.enum';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { FocusHelper } from '../../../../Common/Forms/helpers/focus.helper';
import { SearchMode } from '../../enums/search-mode.enum';
import { DuplicatesStatusEnum } from '../../../../Common/Filter/enums/duplicates-status.enum';
import { MonitoringAdvancedBooleanModel } from '../../models/monitoring-advanced-boolean.model';
import { MonitoringKeywordGroupComponent } from '../MonitoringKeywordGroup/monitoring-keyword-group.component';
import { JournodexGeographyModel } from '../../../../Common/Journodex/models/journodex-geography.model';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { BasicNumberFilterModel } from '../../../../api/mentions/model/basic-number-filter.model';
import { ConflictingKeywordGroupValidationModel } from '../../models/conflicting-keyword-group-validation.model';
import { AttributeModel } from '@Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '@Common/Misc/enums/attribute-types.enum';
import { TopicBackfillDialogComponent } from '../TopicBackfillDialog/topic-backfill-dialog.component';
import { TopicBackfillApi } from '@api/backfills/topic-backfill.api';
import { TopicWorkflowFormGroup } from '../TopicWorkflowSettings/topic-workflow-settings.component';
import { TopicBackfillModel } from '../../models/topic-backfill.model';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { SaveAsGroupDialogComponent } from '../../../../Common/Sources/components/SaveAsGroupDialog/save-as-group-dialog.component';
import { SourceGroup } from '@Common/Sources/models/source-group.model';
import { DiscoverListFilterModel } from '@api/mentions/model/discover-list-filter.model';
import { UpsellDialogType, UpsellInfoDialogComponent } from '@Common/Dialogs/components/UpsellInfoDialog/upsell-info-dialog.component';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { DataSelectTakeoverPanelComponent } from '../../../../Common/Dialogs/components/DataSelectTakeoverPanel/data-select-takeover-panel.component';
import { TargetingSearchesApiService } from '@api/targeting/targeting-searches.api.service';
import { AutoSavingConfigModel } from '@Monitor/Shared/models/auto-saving-config.model';
import { MonitoringViewModesEnum } from '@Monitor/Shared/enums/monitoring-view-modes.enum';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { UserAppPreferencesModel } from '@Core/Users/models/user-app-preferences.model';
import { UserAppMonitoringDisplaySettingsModel } from '@Core/Users/models/user-app-default-display-preferences.model';
import { AgentsApiService } from '../../../../api/agents/agents.api';
import { topicAgentSystemPrompt } from '../../../../api/agents/models/topic-agent-system-prompt.api.model';
import { SavedMentionSearchApiModel } from '../../../../api/mentions/model/saved-mention-search.api.model';
import { AIContextTakeoverComponent } from '../../../../Common/Dialogs/components/AIContextTakeover/ai-context-takeover.component';
import { ProgressDialogComponent } from '../../../../Common/Dialogs/components/ProgressDialog/progress-dialog.component';
import { ProgressStageModel } from '../../../../Common/Dialogs/models/progress.model';
import { MediaTypeEnum } from '../../../../Common/Basic/enums/media-type.enum';
import { BroadcastSubTypeEnum } from '../../../../Common/Basic/enums/broadcast-sub-type.enum';
import { FilterOptionModel } from '../../../../Common/Filter/models/filter-option.model';

interface MonitoringSearchForm {
    requiredSource: FormGroup<RequiredSourceGroup>;
    name: FormControl<string>;
    topicWorkFlowSettings: FormGroup<TopicWorkflowFormGroup>;
    mediaTypes: FormControl<MediaTypeModel[]>;
    regions: FormGroup<RegionGroup>;
    languages: FormGroup<LanguageGroup>;
    sentiments: FormControl<SentimentModel[]>;
    caseSensitive: FormControl<boolean>;
    hideSimilar: FormControl<boolean>;
    dateQuery: FormControl<DateQueryModel|null>;
    searchIntent: FormControl<string>;
}

interface RequiredSourceGroup {
    keywords: FormGroup<KeywordGroup>;
    sources: FormGroup<SourcesGroup>;
    folderTag: FormGroup<FolderTagGroup>;
    discoverListFilter: FormControl<DiscoverListFilterModel>;
}

interface KeywordGroup {
    keywordGroups: FormArray<FormControl<MonitoringKeywordGroupModel>>;
    expertBooleanQuery: FormControl<MonitoringAdvancedBooleanModel>;
}
interface RegionGroup {
    includedRegions: FormControl<JournodexGeographyModel[]>;
    excludedRegions: FormControl<JournodexGeographyModel[]>;
}

interface SourcesGroup {
    includedSources: FormControl<JournodexSourceModel[]>;
    excludedSources: FormControl<JournodexSourceModel[]>;
}

interface LanguageGroup {
    includedLanguages: FormControl<LanguageModel[]>;
    excludedLanguages: FormControl<LanguageModel[]>;
}

interface FolderTagGroup {
    folderTagInclude: FormControl<MultiselectAutocompleteModel<Folder|TagModel>[]>;
    folderTagExclude: FormControl<MultiselectAutocompleteModel<Folder|TagModel>[]>;
}

interface DefaultViewSettings {
    defaultView: MonitoringViewModesEnum;
    defaultSettings?: UserAppMonitoringDisplaySettingsModel;
}

enum SearchTabsEnum {
    newCoverage = 0,
    savedCoverage = 1
}

@Component({
    selector: 'agpr-monitoring-search',
    templateUrl: './monitoring-search.component.html',
    styleUrls: ['./monitoring-search.component.scss'],
    providers: [
        MonitoringSearchLanguageSelectDialogService,
        MonitoringSearchRegionSelectDialogService,
    ],
    standalone: false
})
export class MonitoringSearchComponent implements OnDestroy {

    @ViewChildren(MonitoringKeywordGroupComponent) keywordGroupList: QueryList<MonitoringKeywordGroupComponent>;
    @ViewChild('rhpSidebar') rhpSidebar: RightSidebarComponent;

    // When hosted inside the edit-topic takeover panel rather than mounted on a route. Forces the
    // saved-topic editor behaviour and makes the Search action close the panel instead of relying
    // solely on route navigation.
    @Input() panelMode: boolean = false;
    @Output() searchRequested: EventEmitter<void> = new EventEmitter<void>();
    @Output() cancelRequested: EventEmitter<void> = new EventEmitter<void>();

    hasAgent: boolean = false;
    hasAgentDebug: boolean = false;
    naturalLanguagePrompt: string = '';
    taskDescription: string = topicAgentSystemPrompt;
    isRefining: boolean = false;

    topicSystemPrompt: string = '';
    systemPrompt: string = '';

    searchForm: FormGroup<MonitoringSearchForm>;
    search: MentionSearchModel = null;
    isDirty: boolean;
    isDiscoverListFilterDirty: boolean = false;

    showErrors: boolean = false;
    hasAdminPermission: boolean = false;
    hasTargeting: boolean = false;
    isPaMedia: boolean = false;
    showSaveIncludeSources: boolean = false;
    showSaveExcludeSources: boolean = false;
    hasEmptyKeywordGroup: boolean = true;
    isImageSearch: boolean = false;
    saveInProgress: boolean = false;
    backfillAttributes: AttributeModel[][] = [];
    backfills: TopicBackfillModel[];

    selectedTabIndex: SearchTabsEnum = SearchTabsEnum.newCoverage;
    dateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();

    expertQueryError: string;
    searchModelButtonLabel: string = 'Monitoring.keyWordGroup.enableExpertMode';
    expertBooleanSearchEnabled: boolean = false;

    currentRoute: MonitoringRouteModel;
    searchRange: SearchRangeModel = null;
    tags: TagModel[] = [];
    folders: Folder[] = [];
    overlayContainer: Element;

    showSection: boolean = true;
    userPrefs: UserAppPreferencesModel;

    manageSourceDialog = ManageSourcesDialogComponent;

    private topics: MentionSearchListItemModel[];
    private subscriptions: Subscription[] = [];

    readonly id = 'monitoringSearch';
    readonly buttonTypes = ButtonTypesEnum;
    readonly routeTypes = RouteType;
    readonly includeOrExclude = IncludeOrExclude;
    readonly seriesTypeEnum = SeriesTypeEnum;
    readonly searchTab = SearchTabsEnum;
    readonly tabsStyleTypesEnum = TabsStyleTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly availableSentiments: SentimentModel[] = SentimentModel.getAll();
    readonly sourceContactLimit = 1000;

    readonly regionsAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.regionsDataSource.searchGeographicLocations.bind(this.regionsDataSource)
    };
    readonly languagesAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.localizationService.autocompleteLanguages.bind(this.localizationService)
    };
    readonly excludedSourceOptions: JournodexTypeModel[] = [
        new JournodexTypeModel(JournodexSourceTypeEnum.none, 'Monitor.none', true),
        new JournodexTypeModel(JournodexSourceTypeEnum.contacts, 'Common.journodex.sourceTypes.contacts'),
        new JournodexTypeModel(JournodexSourceTypeEnum.outlets, 'Common.journodex.sourceTypes.outlets'),
        new JournodexTypeModel(JournodexSourceTypeEnum.urls, 'Common.journodex.sourceTypes.urls'),
        new JournodexTypeModel(JournodexSourceTypeEnum.sourceGroups, 'Common.journodex.sourceTypes.sourceGroups'),
    ];
    readonly includedSourceOptions: JournodexTypeModel[] = [
        new JournodexTypeModel(JournodexSourceTypeEnum.all, 'Monitor.all', true),
        new JournodexTypeModel(JournodexSourceTypeEnum.contacts, 'Common.journodex.sourceTypes.contacts'),
        new JournodexTypeModel(JournodexSourceTypeEnum.outlets, 'Common.journodex.sourceTypes.outlets'),
        new JournodexTypeModel(JournodexSourceTypeEnum.urls, 'Common.journodex.sourceTypes.urls'),
        new JournodexTypeModel(JournodexSourceTypeEnum.sourceGroups, 'Common.journodex.sourceTypes.sourceGroups'),
    ];
    readonly folderMultiselectAutocompleteModel = new MultiselectAutocompleteModel<Folder>({
        type: SeriesTypeEnum.folder,
        headerLabel: 'Monitoring.source.type.folder',
        placeholder: 'Monitoring.search.selectAFolder',
        browseAll: false,
        value: [],
        isAutocomplete: true,
        autocompleteDropdown: true,
        labelLookup: this.folderService.getFoldersByIds.bind(this.folderService),
        labelLookupKey: 'id',
        labelLookupValue: 'name',
        suggestionsFetcher: this.folderService.quicksearchFoldersByName.bind(this.folderService)
    });
    readonly tagMulitselectAutocompleteModel = new MultiselectAutocompleteModel<TagModel>({
        type: SeriesTypeEnum.tag,
        headerLabel: 'Monitoring.source.type.tag',
        placeholder: 'Monitoring.search.selectATag',
        browseAll: false,
        value: [],
        isAutocomplete: true,
        autocompleteDropdown: true,
        labelLookup: this.tagsService.getTagsByIds.bind(this.tagsService),
        labelLookupKey: 'id',
        labelLookupValue: 'name',
        suggestionsFetcher: this.tagsService.quicksearchTagsByName.bind(this.tagsService)
    });
    readonly saveActions: MenuItem[] = [
        {
            label: 'Monitoring.search.saveChanges',
            command: () => this.save()
        },
        {
            label: 'Monitoring.saveAsNewTopic',
            command: () => this.saveAs(true)
        }
    ];

    constructor(
        private monitoringRoutingService: MonitoringRoutingService,
        private regionsDataSource: JournodexRegionsService,
        private mentionSearchService: MentionSearchService,
        private monitorConfigService: MonitorConfigService,
        private localizationService: LocalizationService,
        private messageService: MessageService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private languageSelectDialogService: MonitoringSearchLanguageSelectDialogService,
        private regionSelectDialogService: MonitoringSearchRegionSelectDialogService,
        private coreDialogService: CoreDialogService,
        private topicsService: TopicsService,
        private searchResultsService: SearchResultsService,
        private currentUserStateService: CurrentUserStateService,
        private folderService: FolderService,
        private tagsService: TagsService,
        private el: ElementRef,
        private topicBackfillApi: TopicBackfillApi,
        private configService: ConfigService,
        private targetingSearchesApiService: TargetingSearchesApiService,
        private userPreferencesService: UserPreferencesService,
        private agentsApiService: AgentsApiService
    ) {
        if (this.configService.brandGUIDIsRefMedia()) {
            this.showSection = false;
        }
        this.searchForm = new FormGroup<MonitoringSearchForm>({
            requiredSource: new FormGroup({
                keywords: new FormGroup({
                    keywordGroups: new FormArray<FormControl<MonitoringKeywordGroupModel>>([],[
                        this.keywordGroupsValidator()
                    ]),
                    expertBooleanQuery: new FormControl<MonitoringAdvancedBooleanModel>({
                        value: new MonitoringAdvancedBooleanModel(),
                        disabled: true
                    },[
                        this.expertBooleanQueryValidator(),
                        this.expertMinLengthValidator(),
                        this.expertPureNotValidator()
                    ]),
                }, {
                    validators: [
                        this.searchContextValidator(),
                        this.keywordsMaxLengthValidator()
                    ]
                }),
                folderTag: new FormGroup({
                    folderTagInclude: new FormControl<MultiselectAutocompleteModel<Folder|TagModel>[]>([]),
                    folderTagExclude: new FormControl<MultiselectAutocompleteModel<Folder|TagModel>[]>([]),
                },{
                    validators: this.folderTagValidator()
                }),
                sources: new FormGroup({
                    includedSources: new FormControl<JournodexSourceModel[]>([]),
                    excludedSources: new FormControl<JournodexSourceModel[]>([])
                }),
                discoverListFilter: new FormControl<DiscoverListFilterModel>(null, {
                    validators: [this.discoverListValidator(), this.discoverListCountValidator()]
                })
            },{
                validators: this.sourceRequiredValidator()
            }),
            name: new FormControl<string>({
                value: null,
                disabled: true
            }, [
                this.duplicateNameValidator(),
                Validators.required
            ]),
            topicWorkFlowSettings: new FormGroup({
                autoTag: new FormGroup({
                    isAutoTaggingSearch: new FormControl<boolean>(null),
                    tagIds: new FormControl<number[]>([])}
                ),
                autoSave: new FormGroup({
                    saveEnabled: new FormControl<boolean>(false),
                    startDate: new FormControl<Moment> (null),
                    endDate: new FormControl<Moment> (null),
                    tagIds: new FormControl<number[]> ([]),
                    folderIds: new FormControl<number[]> ([]),
                    timezone: new FormControl<string>(null)
                })
            }),
            mediaTypes: new FormControl<MediaTypeModel[]>([], [
                Validators.required,
                this.broadcastSubTypeValidator()
            ]),

            regions: new FormGroup({
                includedRegions: new FormControl<JournodexGeographyModel[]>([]),
                excludedRegions: new FormControl<JournodexGeographyModel[]>([]),
            },{
                validators: this.regionsValidator()
            }),
            languages: new FormGroup({
                includedLanguages: new FormControl<LanguageModel[]>([]),
                excludedLanguages: new FormControl<LanguageModel[]>([]),
            }),
            sentiments: new FormControl<SentimentModel[]>(SentimentModel.getAll()),
            caseSensitive: new FormControl<boolean>(false),
            hideSimilar: new FormControl<boolean>(false),
            dateQuery: new FormControl<DateQueryModel>(null),
            searchIntent: new FormControl<string>('')
        });

        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().subscribe(
                route => {
                    this.currentRoute = route;
                }
            ),
            this.currentUserStateService.getPermissions().subscribe(
                permissions => {
                    this.hasAdminPermission = (permissions?.includes(userPermissionsEnum.advancedMonitoringManager));
                    this.hasTargeting = permissions?.includes(userPermissionsEnum.advancedTargeting);
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(() => {
                this.hasAgent = this.currentUserStateService.agenticFeatureEnabled();
                this.hasAgentDebug = this.currentUserStateService.debugTopicAgenticWorkflowEnabled();
            }),
            combineLatest([
                this.topicsService.getFocusedTopic().pipe(filter(x => !!x)),
                this.monitorConfigService.getAvailableMediaTypes().pipe(filter(x => x.length !== 0 || !!x)),
                this.monitorConfigService.getAvailableSocialMediaTypes().pipe(filter(x => x.length !== 0 || !!x)),
                this.folderService.all$.pipe(tap(folders => this.folders = folders)),
                this.tagsService.all$.pipe(tap(tags => this.tags = tags)),
            ]).subscribe(([search, provisionedMediaTypes, provisionedSocialMediaTypes]) => {
                let mediaTypes = search.searchCriteria.query.includedMediaTypes.filter(mediaType => {
                    return provisionedMediaTypes.find(x => x === mediaType.id) || provisionedSocialMediaTypes.find(x => x === mediaType.id) ||
                            mediaType.isSavedSearch && search.searchCriteria.isAccountSearch;
                });

                // For existing saved searches, if no TV subtypes are selected, default to all subtypes.
                const tvSubTypes = search.searchCriteria.query.includedTvTypes?.length
                    ? search.searchCriteria.query.includedTvTypes
                    : MediaTypeModel.getTvSubTypes();
                this.addTvSubTypes(mediaTypes, tvSubTypes);

                if (mediaTypes.length === 0) {
                    // No media types means "all provisioned" — default them all on without marking dirty
                    mediaTypes = MediaTypeModel.getAllMediaTypes().filter(mediaType => {
                        return provisionedMediaTypes.find(x => x === mediaType.id) ||
                               provisionedSocialMediaTypes.find(x => x === mediaType.id) ||
                               (mediaType.isSavedSearch && search.searchCriteria.isAccountSearch);
                    });

                    this.addTvSubTypes(mediaTypes, MediaTypeModel.getTvSubTypes([BroadcastSubTypeEnum.PROGRAM]));

                    if (this.topicsService.getCachedTopic) {
                        this.topicsService.getCachedTopic.searchCriteria.query.includedMediaTypes = mediaTypes;
                    }
                }

                this.populateFormFromTopic(search, mediaTypes);

                if (this.search.searchCriteria.isAccountSearch) {
                    this.selectedTabIndex = SearchTabsEnum.savedCoverage;
                    this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
                } else {
                    this.selectedTabIndex = SearchTabsEnum.newCoverage;
                    this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
                }

                this.reloadBackfills();
            }),
            this.topicsService.getTopics().subscribe(
                topics => {
                    this.topics = topics;
                }
            ),
            this.topicsService.getDirtyFlag().subscribe(
                isDirty => {
                    this.isDirty = isDirty;
                }
            ),
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                }
            ),
            this.searchForm.get('languages').valueChanges.subscribe(lang => {
                // only allow users to add languages to either include or exclude but not both as this doesn't rationaly follow
                // eg. "Give me English, don't give me French" is the same as saying "Give me English"

                if (lang.includedLanguages?.length > 0) {
                    this.excludeLangs.disable({ onlySelf: true, emitEvent: false });
                } else if (lang.excludedLanguages?.length > 0) {
                    this.includeLangs.disable({ onlySelf: true, emitEvent: false });
                } else {
                    this.includeLangs.enable({ onlySelf: true, emitEvent: false });
                    this.excludeLangs.enable({ onlySelf: true, emitEvent: false });
                }
            }),
            this.searchForm.get('caseSensitive').valueChanges.subscribe(x => {
                this.keywordGroups.controls.forEach(control => control.updateValueAndValidity());
                this.keywordGroups.updateValueAndValidity();
            }),
            this.searchForm.get('requiredSource.keywords').valueChanges.subscribe(x => {
                this.hasEmptyKeywordGroup = this.keywordGroups.value.some(group => !group.isAnyKeywordSet());
            }),
            this.searchForm.get('requiredSource.sources').valueChanges.subscribe(value => {
                this.showSaveIncludeSources = value.includedSources.length >= 1
                && value.includedSources.filter(source => {
                    return source.entityType !== SourceTypeEnum.sourceGroups;
                }).length >= 1;

                this.showSaveExcludeSources = value.excludedSources.length >= 1
                && value.excludedSources.filter(source => {
                    return source.entityType !== SourceTypeEnum.sourceGroups;
                }).length >= 1;
            }),
            // only subscribe tofocused topic and patch value for auto-tagging here to avoid setting incorrect value when tag list is updated
            this.topicsService.getFocusedTopic().pipe(
                filter(x => !!x),
                tap(search => {
                    this.searchForm.patchValue({
                        topicWorkFlowSettings: {
                            autoTag: {
                                isAutoTaggingSearch: search.autoTaggingSearch,
                                tagIds: search.applyTagIds
                            },
                            autoSave: {
                                saveEnabled: search.autoSavingConfig?.enabled,
                                startDate: search.autoSavingConfig?.startDate,
                                endDate: search.autoSavingConfig?.endDate,
                                tagIds: search.autoSavingConfig?.tagIds,
                                folderIds: search.autoSavingConfig?.folderIds,
                                timezone: search.autoSavingConfig?.timezone
                            }
                        }
                    });
                })
            ).subscribe(),
            this.searchForm.valueChanges.pipe(debounceTime(300)).subscribe(formValues => {
                this.determineIfImageSearch(formValues);
                this.updateSearch(formValues);
            }),
            this.userPreferencesService.getUserAppPreferences().subscribe(userAppPrefs => {
                this.userPrefs = userAppPrefs;
            }),
        );
        this.overlayContainer = document.getElementById('main-content');
        this.isPaMedia = !!this.configService.brandGUIDIsPaMedia();
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (this.isDirty && this.selectedTabIndex !== SearchTabsEnum.savedCoverage) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    /**
     * True when editing an existing saved topic — either mounted on a topic route or hosted in the
     * takeover panel with a saved topic loaded. Drives the topic-only UI (refine banner, Undo/Save
     * actions, topic settings tab). In the panel this is keyed off the loaded topic's id so the same
     * panel can also host an ad-hoc search (no id → create mode). Computed lazily so it reflects
     * `panelMode`/`search`, which are set after construction.
     */
    get savedTopic(): boolean {
        if (this.panelMode) {
            return !!this.search?.id;
        }
        return this.currentRoute?.type === RouteType.topicUpdate
            || this.currentRoute?.type === RouteType.topic;
    }

    /**
     * True when the takeover panel is hosting an ad-hoc search (the "Edit Search" experience) rather
     * than a saved topic. Surfaces the create-flow UI (coverage tabs, create CoPilot banner, Reset
     * All, Save Search As Topic) that is otherwise gated on the search routes.
     */
    get panelNewSearch(): boolean {
        return this.panelMode && !this.search?.id;
    }

    /**
     * Show the Agentic (PR CoPilot™) upsell banner to users who are not provisioned for the feature,
     * in the same New Coverage search contexts where the CoPilot agent banner would otherwise appear
     * (the New Search route/panel and the Edit Topic / Search takeover panel).
     */
    get showAgenticUpsell(): boolean {
        return !this.hasAgent
            && this.selectedTabIndex === SearchTabsEnum.newCoverage
            && (this.panelMode
                || this.currentRoute?.type === RouteType.search
                || this.currentRoute?.type === RouteType.updateSearch);
    }

    /**
     * Agentic upsell copy: the takeover panel is framed as "Edit Search", so it uses the edit-variant
     * copy; the standalone New Search page uses the build-a-new-search copy.
     */
    get agenticUpsellHeading(): string {
        return this.panelMode ? 'Common.agenticUpsell.edit.heading' : 'Common.agenticUpsell.search.heading';
    }

    get agenticUpsellMessage(): string {
        return this.panelMode ? 'Common.agenticUpsell.edit.message' : 'Common.agenticUpsell.search.message';
    }

    get name(): FormControl {
        return this.searchForm.get('name') as FormControl;
    }

    get mediaTypes(): FormControl {
        return this.searchForm.get('mediaTypes') as FormControl;
    }

    get requiredSource(): FormGroup {
        return this.searchForm.get('requiredSource') as FormGroup;
    }

    get keywords(): FormGroup {
        return this.searchForm.get('requiredSource.keywords') as FormGroup;
    }

    get keywordGroups(): FormArray<FormControl<MonitoringKeywordGroupModel>> {
        return this.searchForm.get('requiredSource.keywords.keywordGroups') as FormArray;
    }

    get expertKeywords(): FormControl<MonitoringAdvancedBooleanModel> {
        return this.searchForm.get('requiredSource.keywords.expertBooleanQuery') as FormControl;
    }

    get regions(): FormGroup {
        return this.searchForm.get('regions') as FormGroup;
    }

    get folderTag(): FormGroup {
        return this.searchForm.get('requiredSource.folderTag') as FormGroup;
    }

    get folderTagInclude(): FormControl {
        return this.searchForm.get('requiredSource.folderTag.folderTagInclude') as FormControl;
    }

    get languages(): FormGroup {
        return this.searchForm.get('languages') as FormGroup;
    }

    get includeLangs(): FormControl {
        return this.searchForm.get('languages.includedLanguages') as FormControl;
    }

    get excludeLangs(): FormControl {
        return this.searchForm.get('languages.excludedLanguages') as FormControl;
    }

    get excludedSources(): FormControl {
        return this.searchForm.get('requiredSource.sources.excludedSources') as FormControl;
    }

    get includedSources(): FormControl {
        return this.searchForm.get('requiredSource.sources.includedSources') as FormControl;
    }

    get caseSensitive(): FormControl {
        return this.searchForm.get('caseSensitive') as FormControl;
    }

    get discoverList(): FormGroup {
        return this.searchForm.get('requiredSource.discoverListFilter') as FormGroup;
    }

    /**
     * Updates the search model with the current form values & updates the dirty flag
     * @param formValues - form model of type MonitoringSearchForm
     * Note: shortcircuit logic is needed when form control is disabled and results in undefined
     */
    private updateSearch(formValues) {
        const newSearch = this.search.clone(false);
        const query = new MentionSearchQueryModel();

        query.keywordGroups = formValues.requiredSource.keywords.keywordGroups || [new MonitoringKeywordGroupModel()];
        query.expertBooleanQuery = formValues.requiredSource.keywords.expertBooleanQuery || new MonitoringAdvancedBooleanModel();
        query.searchMode = (query.expertBooleanQuery.filter.length > 0) ? SearchMode.expert : SearchMode.standard;

        if (this.search.searchCriteria.isAccountSearch) {
            query.folder = <BasicNumberFilterModel>{
                include: this.getFolderTagValues(formValues.requiredSource.folderTag.folderTagInclude, SeriesTypeEnum.folder),
                exclude: this.getFolderTagValues(formValues.requiredSource.folderTag.folderTagExclude, SeriesTypeEnum.folder)
            };
            query.tag = <BasicNumberFilterModel>{
                include: this.getFolderTagValues(formValues.requiredSource.folderTag.folderTagInclude, SeriesTypeEnum.tag),
                exclude: this.getFolderTagValues(formValues.requiredSource.folderTag.folderTagExclude, SeriesTypeEnum.tag),
            };
        } else {
            query.folder = { include: [], exclude: [] };
            query.tag = { include: [], exclude: [] };
        }

        const mediaTypes: MediaTypeModel[] = formValues.mediaTypes;
        query.includedMediaTypes = mediaTypes;

        const tvMediaType = mediaTypes.find(mediaType => mediaType.id === MediaTypeEnum.TV);
        if (tvMediaType) {
            query.includedTvTypes = tvMediaType.children?.map(option => option.value) ?? [];
        }

        query.includedSources = formValues.requiredSource.sources.includedSources || [];
        query.excludedSources = formValues.requiredSource.sources.excludedSources || [];

        // MentionSearchQueryModel.getApiEntity has same functionality.
        // this is needed before dirty checking to prevent false positive
        const include = query.includedSources.filter(x => x.entityType === SearchCriteriaSourcesEnum.sourceGroups);
        const exclude = query.excludedSources.filter(x => x.entityType === SearchCriteriaSourcesEnum.sourceGroups);
        query.sourceGroup.include = include.map(x => +x.entityName);
        query.sourceGroup.exclude = exclude.map(x => +x.entityName);

        query.includedRegions = formValues.regions.includedRegions;
        query.excludedRegions = formValues.regions.excludedRegions;

        query.includedLanguages = formValues.languages.includedLanguages || [];
        query.excludedLanguages = formValues.languages.excludedLanguages || [];

        query.sentiments = formValues.sentiments;
        query.caseSensitive = formValues.caseSensitive;
        query.dateQuery = formValues.dateQuery;
        query.discoverListFilter = formValues.requiredSource.discoverListFilter;
        newSearch.searchCriteria = this.search.searchCriteria.clone();
        newSearch.searchCriteria.showSimilar = !formValues.hideSimilar;
        newSearch.searchCriteria.query = query;

        newSearch.name = formValues.name;

        newSearch.autoTaggingSearch = formValues.topicWorkFlowSettings.autoTag.isAutoTaggingSearch;
        newSearch.applyTagIds = formValues.topicWorkFlowSettings.autoTag.tagIds;

        newSearch.autoSavingConfig = new AutoSavingConfigModel(this.search.toApiModel());
        newSearch.autoSavingConfig.enabled = formValues.topicWorkFlowSettings.autoSave.saveEnabled;
        newSearch.autoSavingConfig.folderIds = formValues.topicWorkFlowSettings.autoSave.folderIds;
        newSearch.autoSavingConfig.tagIds = formValues.topicWorkFlowSettings.autoSave.tagIds;
        newSearch.autoSavingConfig.startDate = formValues.topicWorkFlowSettings.autoSave.startDate;
        newSearch.autoSavingConfig.endDate = formValues.topicWorkFlowSettings.autoSave.endDate;
        newSearch.autoSavingConfig.timezone = formValues.topicWorkFlowSettings.autoSave.timezone;

        newSearch.searchIntent = formValues.searchIntent || null;

        this.search = newSearch;

        let dirty = false;
        if (this.topicsService.getCachedTopic) {
            if (this.search.equals(this.topicsService.getCachedTopic, true, false, true, true) && this.isDiscoverListFilterDirty === false) {
                dirty = false;
            } else {
                dirty = true;
            }
        }

        this.topicsService.setDirtyFlag(dirty);
    }


    /**
     * Saves changes made to a topic
     */
    private save() {
        this.showErrors = !this.searchForm.valid;

        if (this.showErrors || this.saveInProgress) {
            if (this.showErrors) {
                const firstTabInvalid = [
                    'mediaTypes', 'regions', 'languages', 'sentiments',
                    'requiredSource.folderTag', 'requiredSource.sources', 'requiredSource.discoverListFilter'
                ].some(key => this.searchForm.get(key)?.invalid);
                const secondTabInvalid = ['topicWorkFlowSettings', 'searchIntent'].some(key => this.searchForm.get(key)?.invalid);

                if (firstTabInvalid) {
                    this.rhpSidebar.selectTabByIndex(0);
                } else if (secondTabInvalid) {
                    this.rhpSidebar.selectTabByIndex(1);
                }
            }
            return;
        }

        this.saveInProgress = true;
        const currentSearch$: Observable<MentionSearchModel> = this.setDiscoverListFilterSearchId();
        currentSearch$.pipe(
            switchMap(search => {
                return this.topicsService.updateTopic(search).pipe(
                    finalize(() => this.saveInProgress = false)
                );
            })).subscribe(savedTopic => {
            if (this.panelMode) {
                // Saving from the takeover persists the changes, then refreshes the search with the
                // saved criteria and closes the panel.
                if (!this.searchResultsService.filtersLocked()) {
                    this.mentionSearchService.setDateQuery(savedTopic.searchCriteria.query.dateQuery);
                    // showSimilar is not persisted on the topic, so the reloaded savedTopic no longer
                    // carries it. Apply the form's hide-similar choice (this.search) to the runtime
                    // filter, mirroring performSearch, so saving reflects the user's selection.
                    this.mentionSearchService.setDuplicateFilter(
                        this.search.searchCriteria.showSimilar ? DuplicatesStatusEnum.show : DuplicatesStatusEnum.hide
                    );
                }
                this.runSearchAndClosePanel(savedTopic);
            }
        });
    }

    /**
     * Saves search as new topic
     * @param newTopic when true, clone and remove the topic name
     */
    saveAs(newTopic: boolean = false) {
        FocusHelper.focusFirstError(this.el.nativeElement);

        this.showErrors = !this.searchForm.valid;
        if (this.showErrors) {
            return;
        }
        const currentSearch$: Observable<MentionSearchModel> = this.setDiscoverListFilterSearchId();
        currentSearch$.pipe(
            tap(search => {
                this.coreDialogService.open(
                    SaveTopicDialogComponent,
                    {
                        inputs: {
                            search: search,
                            saveAsNew: newTopic,
                            currentGroup: this.search.groupId
                        }
                    }
                );
            })
        ).subscribe();
    }

    private addTvSubTypes(mediaTypes: MediaTypeModel[] = [], tvSubTypes: MediaTypeModel[] = []) {
        const tvMediaType = mediaTypes.find(mediaType => mediaType.id === MediaTypeEnum.TV);
        if (tvMediaType) {
            tvMediaType.children = tvSubTypes.map(subType => ({
                label: subType.name,
                value: subType
            } as FilterOptionModel));
        }
    }

    /**
     * Sets the current search as the focused search and navigates to the results
    */
    performSearch() {
        FocusHelper.focusFirstError(this.el.nativeElement);

        this.showErrors = !this.searchForm.valid;
        if (this.showErrors) {
            return;
        }

        // set date query and hide-similar (duplicates) filter only if filters are not locked.
        // Both drive the runtime filter rather than the saved topic.
        if (!this.searchResultsService.filtersLocked()) {
            this.mentionSearchService.setDateQuery(this.search.searchCriteria.query.dateQuery);
            this.mentionSearchService.setDuplicateFilter(
                this.search.searchCriteria.showSimilar ? DuplicatesStatusEnum.show : DuplicatesStatusEnum.hide
            );
        }

        this.topicsService.setDirtyFlag(this.isDirty);

        const currentSearch$: Observable<MentionSearchModel> = this.setDiscoverListFilterSearchId();
        currentSearch$.subscribe(search => {
            this.topicsService.setCurrentResource(search);

            if (this.panelMode) {
                this.runSearchAndClosePanel(search);
                return;
            }

            const { defaultView, defaultSettings } = this.getDefaultViewAndSettings(search.isSaved(), this.search.searchCriteria.isAccountSearch);

            if (defaultSettings) {
                this.searchResultsService.setCurrentDisplaySettings(defaultSettings);
            }

            this.getRouteByDefaultView(defaultView, search.isSaved(), search);
        });
    }

    /**
     * Panel mode only: apply the (saved or unsaved) search criteria, run the search, navigate to the
     * results view and signal the host to tear down the takeover. The panel is usually open over the
     * same results route the search would navigate to, where router navigation is a no-op and would
     * not re-run the search — so the search is applied explicitly here.
     */
    private runSearchAndClosePanel(search: MentionSearchModel): void {
        const { defaultSettings } = this.getDefaultViewAndSettings(search.isSaved(), search.searchCriteria.isAccountSearch);

        if (defaultSettings) {
            this.searchResultsService.setCurrentDisplaySettings(defaultSettings);
        }

        this.topicsService.applyTopicSearch(search);
        // Stay on the view/tab the user was on (Mention feed / Syndicates / Analytics) rather than
        // jumping to their default view.
        this.getRouteByDefaultView(this.getCurrentViewMode(), search.isSaved(), search);
        this.searchRequested.emit();
    }

    /**
     * The view mode (Mention feed / Syndicates / Analytics) currently shown behind the takeover,
     * derived from the active route so a panel Search/Save keeps the user on the same tab.
     */
    private getCurrentViewMode(): MonitoringViewModesEnum {
        switch (this.currentRoute?.type) {
            case RouteType.topicAnalyticsSyndicates:
            case RouteType.adHocAnalyticsSyndicates:
                return MonitoringViewModesEnum.SYNDICATE;
            case RouteType.topicAnalyticsDashboard:
            case RouteType.adHocAnalyticsDashboard:
                return MonitoringViewModesEnum.DASHBOARD;
            default:
                return MonitoringViewModesEnum.MENTION;
        }
    }

    /**
     * Reset the topic search to the default state (blank topic)
     */
    resetAll() {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Common.searches.confirmResetHeader',
                message: 'Common.searches.confirmResetMessage',
                cancelLabel: 'Monitoring.cancelButtonLabel',
                okLabel: 'Monitoring.reset',
                id: 'Monitoring.resetCancelButton'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }

                    this.topicsService.resetTopicToDefault();

                    const successMessage: ToastMessageOptions = {
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Monitoring.search.resetHeading'),
                        detail: this.localizationService.transform('Monitoring.search.resetMessage')
                    };
                    this.messageService.add(successMessage);
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    /**
     * Undo changes by reset topic to last saved state (aka cached topic)
     */
    undo() {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Monitoring.confirmUndo',
                message: 'Monitoring.search.confirmUndoMessage',
                cancelLabel: 'Monitoring.cancelButtonLabel',
                okLabel: 'Monitoring.undoChanges',
                id: 'Monitoring_undoChanges_Button'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }

                    this.topicsService.resetTopicToCache();

                    const successMessage: ToastMessageOptions = {
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Monitoring.search.undoSuccessHeading'),
                        detail: this.localizationService.transform('Monitoring.search.undoSuccessMessage', {
                            topicName: this.search.name
                        })
                    };
                    this.messageService.add(successMessage);
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    /**
     * Determines the default view mode and display settings to use based on whether the current search is saved or account-based.
     *
     * @returns An object containing the resolved default view and (optional) default settings.
     */
    private getDefaultViewAndSettings(isSaved: boolean, isAccountSearch: boolean): DefaultViewSettings {
        const prefs = isSaved
            ? this.userPrefs.monitoringPrefs.topicsPrefs
            : isAccountSearch
                ? this.userPrefs.monitoringPrefs.savedCoveragePrefs
                : this.userPrefs.monitoringPrefs.topicsPrefs;

        const defaultSettings = prefs?.defaultDisplaySettings;
        const defaultView = defaultSettings?.resultsViewMode ?? this.searchResultsService.getCurrentViewMode();

        return { defaultView, defaultSettings };
    }

    /**
     * Routes the user to the correct monitoring page based on the resolved default view mode and whether the search is saved or ad-hoc.
     */
    private getRouteByDefaultView(defaultView: MonitoringViewModesEnum, isSaved: boolean, search: MentionSearchModel): void {
        if (isSaved) {
            if (defaultView === MonitoringViewModesEnum.MENTION) {
                this.monitoringRoutingService.gotoTopic(search.id);
            } else if (defaultView === MonitoringViewModesEnum.SYNDICATE) {
                this.monitoringRoutingService.gotoTopicAnalyticsSyndicates(search.id);
            } else {
                this.monitoringRoutingService.gotoTopicAnalyticsDashboard(search.id);
            }
        } else {
            if (defaultView === MonitoringViewModesEnum.MENTION) {
                this.monitoringRoutingService.gotoResults();
            } else if (defaultView === MonitoringViewModesEnum.SYNDICATE) {
                this.monitoringRoutingService.gotoAdHocAnalyticsSyndicates();
            }  else {
                this.monitoringRoutingService.gotoAdHocAnalyticsDashboard();
            }
        }
    }

    /**
     * Compares the name value to existing topic names
     * @returns an error object with the key 'duplicateNameError' when invalid, otherwise null
     */
    private duplicateNameValidator(): ValidatorFn {
        return (control: FormControl<string>): ValidationErrors | null => {
            const invalidName = this.topics?.filter(x => x.id !== this.search.id)
                .some(topic => topic.name === control.value);

            return invalidName ? { duplicateNameError: true } : null;
        };
    }

    /**
     * Compares the include & exclude region fields, verifying a region isn't contianed in both
     * @returns an error object with the key 'regionError' when invalid, otherwise null
     */
    private regionsValidator(): ValidatorFn {
        return (group: FormGroup<RegionGroup>): ValidationErrors | null => {

            const includeRegions = group.get('includedRegions').value;
            const excludeRegions = group.get('excludedRegions').value;

            const uniqueRegionMap = new Map<string, boolean>();
            let invalidRegion = false;

            includeRegions.forEach(region => {
                const key = `${region.geographicType}${region.id}`;
                uniqueRegionMap.set(key, true);
            });

            for (const region of excludeRegions) {
                const key = `${region.geographicType}${region.id}`;
                if (uniqueRegionMap.get(key)) {
                    invalidRegion = true;
                    break;
                }
            }

            return invalidRegion ? { regionError: true } : null;
        };
    }

    /**
     * Compares the include & exclude folderTag fields, verifying a folder/tag isn't contianed in both
     * @returns an error object with the key 'folderTagError' when invalid, otherwise null
     */
    private folderTagValidator(): ValidatorFn {
        return (group: FormGroup<FolderTagGroup>): ValidationErrors | null => {

            const folderTagInclude = group.get('folderTagInclude').value;
            const folderTagExclude = group.get('folderTagExclude').value;

            const uniqueIdsMap = new Map<string, boolean>();
            let invalidFolderTag = false;

            folderTagInclude.forEach(mode => {
                mode.value.forEach(value => {
                    uniqueIdsMap.set(`${mode.type}${value.id}`, true);
                });
            });

            folderTagExclude.forEach(mode => {
                for (const value of mode.value) {
                    if (uniqueIdsMap.get(`${mode.type}${value.id}`)) {
                        invalidFolderTag = true;
                        break;
                    }
                }
            });


            return invalidFolderTag ? { folderTagError: true } : null;
        };
    }

    /**
     * Checks that any included TV has at least one broadcast sub type (Programs/Advertisements) selected
     * @returns an error object with the key 'broadcastSubTypeError' when invalid, otherwise null
     */
    private broadcastSubTypeValidator(): ValidatorFn {
        return (control: AbstractControl<MediaTypeModel[]>): ValidationErrors | null => {
            return MediaTypeModel.hasMissingBroadcastSubType(control.value) ? { broadcastSubTypeError: true } : null;
        };
    }

    /**
     * Checks to see if at least 1 source has been added from (FolderTag.include, Keyword, ExpertKeyword or Source.include)
     * @returns an error object with the key 'sourceRequiredError' when invalid, otherwise return null
     */
    private sourceRequiredValidator(): ValidatorFn {
        return (group: FormGroup<RequiredSourceGroup>): ValidationErrors | null => {
            const includeSource = group.get('sources.includedSources').value;
            const includeFolderTag = group.get('folderTag.folderTagInclude').value;
            const keywords = group.get('keywords.keywordGroups').value;
            const expertKeywords = group.get('keywords.expertBooleanQuery').value;
            const discoverListFilter = group.get('discoverListFilter').value;
            const hasFolderTag = includeFolderTag.map(x => x.value).some(x => x.length > 0);
            const hasKeyword = keywords.some(x => x.hasAndOrKeywords());
            const hasValidExpertKeyword = expertKeywords.filter
                && expertKeywords.filter.length > 0
                && !expertKeywords.filter.trim().startsWith('AND NOT')
                && !expertKeywords.filter.trim().startsWith('*');

            const invalidSourceRequired = !(includeSource.length > 0)
                && !hasFolderTag
                && !hasKeyword
                && !discoverListFilter?.discoverList?.include?.length
                && !hasValidExpertKeyword;

            return invalidSourceRequired ? { sourceRequiredError: true } : null;
        };
    }

    private discoverListValidator(): ValidatorFn {
        return (control: FormControl<DiscoverListFilterModel>): ValidationErrors | null => {
            let discoverListRequired = control.value?.discoverListType && control.value?.discoverList?.include?.length === 0;
            if (!control.value?.discoverListType) {
                discoverListRequired = null;
            }
            return discoverListRequired  ? {discoverListRequired: true } : null;
        };
    }
    private discoverListCountValidator(): ValidatorFn {
        return (control: FormControl<DiscoverListFilterModel>): ValidationErrors | null => {
            return control.value?.contactCount && control.value.contactCount > this.sourceContactLimit ? {discoverListCountError: true } : null;
        };
    }

    checkDiscoverListFilterDirty(dirty: boolean) {
        this.isDiscoverListFilterDirty = dirty;
    }

    /**
     * Checks to see if the keyword groups have valid search contexts
     * @returns an error object with the key 'searchContextError' when invalid, otherwise return null
     */
    private searchContextValidator(): ValidatorFn {
        return (group: FormGroup<KeywordGroup>): ValidationErrors | null => {
            const keywordGroups = group.get('keywordGroups').value;
            const expertKeywordGroup = group.get('expertBooleanQuery').value;

            let invalidSearchContext: boolean = true;
            if (group.get('expertBooleanQuery').enabled) {
                invalidSearchContext = expertKeywordGroup.searchContexts.length === 0;
            } else {
                invalidSearchContext = !keywordGroups.every(keyword => keyword.keywordGroupHasValidSearchContext());
            }


            return invalidSearchContext ? { searchContextError: true } : null;
        };
    }

    /**
     * Checks to see if the expery keyword query contains proper quotes and parenthesis usage
     * @returns an error object with the key 'invalidExpertQueryError' when invalid, otherwise return null
     */
    private expertBooleanQueryValidator(): ValidatorFn {
        return (control: FormControl<MonitoringAdvancedBooleanModel>): ValidationErrors | null => {
            const expertKeywordGroup = control.value;
            const invalidQuery = !expertKeywordGroup.queryStructureIsValid();
            this.expertQueryError = expertKeywordGroup.queryValidationError;

            return invalidQuery ? { invalidExpertQueryError: true } : null;
        };
    }

    /**
     * Checks to see if the expert keyword query meets the min length requirement
     * @see {@link MonitoringAdvancedBooleanModel.minLength}
     * @returns an error object with the key 'minLengthError' when invalid, otherwise return null
     */
    private expertMinLengthValidator(): ValidatorFn {
        return (control: FormControl<MonitoringAdvancedBooleanModel>): ValidationErrors | null => {
            const expertKeywordGroup = control.value;
            const invalidKeywordLength = expertKeywordGroup.isQueryTooShort();

            return invalidKeywordLength ? { minLengthError: true } : null;

        };
    }

    /**
     * Checks to see if the expert keyword query is pure Not
     * @see {@link MonitoringAdvancedBooleanModel.minLength}
     * @returns an error object with the key 'pureNot' when invalid, otherwise return null
     */
    private expertPureNotValidator(): ValidatorFn {
        return (control: FormControl<MonitoringAdvancedBooleanModel>): ValidationErrors | null => {
            const expertKeywordGroup = control.value;
            const invalid = expertKeywordGroup.isPureNot();

            return invalid ? { pureNot: true } : null;

        };
    }

    /**
     * Verify conflicting keywords are not present in single keyword group
     * A conflicting keyword is when the same string (equality dependent on case sensitivity setting)
     * is found in an AND/OR field as well as in a NOT field.
     * @returns an error object with the key 'keywordError' when invalid, otherwise return null
     */
    private keywordValidator(): ValidatorFn {
        return (control: FormControl<MonitoringKeywordGroupModel>): ValidationErrors | null => {

            const caseSensitive = control?.parent?.parent?.parent?.parent?.get('caseSensitive')?.value;
            const exclude = control.value.not;
            const include = [...control.value.and, ...control.value.or];

            let invalidKeyword = false;

            for (const keyword of exclude) {
                let word = keyword;
                if (!caseSensitive) {
                    word = word.toLowerCase();
                }

                if (include.includes(word)) {
                    invalidKeyword = true;
                    break;
                }
            }

            return invalidKeyword ? { 'keywordError': true } : null;

        };
    }

    /**
     * Verify conflicting keywords are not present accross all keyword groups & search contexts.
     * A conflicting keyword is when the same string (equality dependent on case sensitivity setting)
     * is found in an AND/OR field and NOT field, within the same search context.
     * @returns an error object with the key 'keywordGroupsError' when invalid, otherwise return null
     */
    private keywordGroupsValidator(): ValidatorFn {
        return (array: FormArray<FormControl<MonitoringKeywordGroupModel>>): ValidationErrors | null => {

            const caseSensitive = array?.parent?.parent?.parent?.get('caseSensitive')?.value;
            const {notMap, andOrMap} = ConflictingKeywordGroupValidationModel.generateMapings(array.value, caseSensitive);

            let invalidKeywordGroup = false;

            for (const [word, excludedConflictingKeywordModels] of notMap) {
                let wordKey = word;
                if (!caseSensitive) {
                    wordKey = word.toLowerCase();
                }

                const includedConflictingKeywordModels = andOrMap.get(wordKey);
                if (!includedConflictingKeywordModels) {
                    continue;
                }

                for (const includedConflict of includedConflictingKeywordModels) {
                    for (const excludedConflict of excludedConflictingKeywordModels) {
                        if (includedConflict.isInConflict(excludedConflict)) {
                            invalidKeywordGroup = true;
                            break;
                        }
                    }
                    if (invalidKeywordGroup) {
                        break;
                    }
                }
            }

            return invalidKeywordGroup ? { 'keywordGroupsError': true } : null;
        };
    }

    /**
     * Checks to see if the keywords meet the max length requirement
     * @see {@link MonitoringAdvancedBooleanModel.maxLength}
     * @returns an error object with the key 'maxLengthError' when invalid, otherwise return null
     */
    private keywordsMaxLengthValidator(): ValidatorFn {
        return (group: FormGroup<KeywordGroup>): ValidationErrors | null => {
            const keywordGroup = group.get('keywordGroups').value;
            const expertKeywordGroup = group.get('expertBooleanQuery').value;

            let invalidKeywordLength: boolean = true;

            if (group.get('expertBooleanQuery').enabled) {
                invalidKeywordLength = expertKeywordGroup.isQueryTooLong();
            } else {
                // @TODO a bit hacky, should provide an isQueryTooLong function in MonitoringKeywordGroupModel
                const query = new MonitoringAdvancedBooleanModel();
                query.mapKeywordGroupQueryToExpertBooleanQuery(
                    keywordGroup,
                    true
                );
                invalidKeywordLength = query.isQueryTooLong();
            }


            return invalidKeywordLength ? { maxLengthError: true } : null;

        };
    }

    /**
     * Opens the select language dialog
     */
    showLanguages(key: keyof LanguageGroup, state: IncludeOrExclude) {
        const languages = this.searchForm.get(`languages.${key}`).value as LanguageModel[];
        this.languageSelectDialogService.prepareModelData(state, languages);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'monitoringSearch.languageSelectDialog',
                dialogService: this.languageSelectDialogService,
                dialogHeader: 'Monitoring.result.languages',
                dialogMessage: 'Monitoring.result.selectLanguagesMsg',
                leftColumnHeader: 'Monitoring.result.availableLanguages',
                rightColumnHeader: 'Monitoring.result.selectedLanguages',
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedLanguages: LanguageModel[]) => {
                    if (!selectedLanguages) {
                        selectedLanguages = [];
                    }

                    this.searchForm.patchValue({
                        languages: {
                            [key]: selectedLanguages
                        }
                    });
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    /**
     * Opens the select regions dialog
     */
    showRegions(key: keyof RegionGroup, state: IncludeOrExclude) {
        const regions = this.searchForm.get(`regions.${key}`).value as JournodexGeographyModel[];
        this.regionSelectDialogService.prepareModelData(state, regions);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'monitoringSearch.regionSelectDialog',
                dialogService: this.regionSelectDialogService,
                dialogHeader: 'Monitoring.search.regions',
                dialogMessage: 'Monitoring.search.selectedRegionsMsg',
                leftColumnHeader: 'Monitoring.search.availableRegions',
                rightColumnHeader: 'Monitoring.search.selectedRegions',
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedRegions: JournodexGeographyModel[]) => {
                    if (!selectedRegions) {
                        selectedRegions = [];
                    }

                    this.searchForm.patchValue({
                        regions: {
                            [key]: selectedRegions
                        }
                    });
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }



    /**
     * Change search mode to either Expert or Standard Mode
     */
    changeSearchMode() {
        // moving from expert to standard mode
        if (this.expertBooleanSearchEnabled) {
            if (this.expertKeywords.value.filter?.length > 0) {
                const converted = this.expertKeywords.value.tryConvertToKeywordGroups();
                if (converted) {
                    this.setSearchModeToStandard(converted);
                } else {
                    // warn about losing EBQ and then switch to standard KG mode
                    const confirmConfig: ConfirmDialogModel = {
                        inputs: {
                            heading: 'Monitoring.ExpertBooleanSearch.SwitchToStandardMode.header',
                            messageHtml: 'Monitoring.ExpertBooleanSearch.SwitchToStandardMode.message',
                            okLabel: 'Monitoring.ExpertBooleanSearch.SwitchToStandardMode.affirmation'
                        },
                        outputs: {
                            dialogClose: (confirmed: boolean) => {
                                if (confirmed) {
                                    this.setSearchModeToStandard();
                                } else {
                                    this.expertBooleanSearchEnabled = true;
                                }
                            }
                        }
                    };
                    this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
                }
            } else {
                this.setSearchModeToStandard();
            }
        } else {
            // moving from standard to expert mode
            if (this.keywordGroups.value.some(x => x.isAnyKeywordSet())) {
                const io: ConfirmDialogModel = {
                    inputs: {
                        heading: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.header',
                        messageHtml: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.possible.message',
                        okLabel: 'Monitoring.ExpertBooleanSearch.SwitchToExpertMode.affirmation'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                const expertQuery = new MonitoringAdvancedBooleanModel();
                                expertQuery.mapKeywordGroupQueryToExpertBooleanQuery(
                                    this.search.searchCriteria.query.keywordGroups
                                );
                                this.keywords.patchValue({
                                    'expertBooleanQuery': expertQuery
                                });
                                this.setSearchModeToExpert();
                            } else {
                                this.expertBooleanSearchEnabled = false;
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);
            } else {
                this.setSearchModeToExpert();
            }
        }
    }

    /**
     * Configure settings to indicate we are using Standard search mode (e.g. Keyword Groups).
     * If convertedGroups are provided they replace the current keyword groups; otherwise existing
     * groups are kept (or a blank one is added if the array is empty).
     */
    private setSearchModeToStandard(convertedGroups?: MonitoringKeywordGroupModel[]): void {
        this.keywords.patchValue({
            'expertBooleanQuery': new MonitoringAdvancedBooleanModel()
        });
        this.expertKeywords.disable();
        this.keywordGroups.enable();
        if (convertedGroups?.length) {
            this.keywordGroups.clear();
            convertedGroups.forEach(group => {
                this.keywordGroups.push(new FormControl(group, [this.keywordValidator()]));
            });
        } else if (!this.keywordGroups.controls.length) {
            this.addKeywordGroup();
        }
        this.expertBooleanSearchEnabled = false;
        this.searchModelButtonLabel = 'Monitoring.keyWordGroup.enableExpertMode';
        this.search.searchCriteria.query.searchMode = SearchMode.standard;
    }

    /**
     * Configure settings to indicate we are using Expert search mode (e.g. Expert Boolean Query)
     */
    private setSearchModeToExpert(): void {
        this.keywords.patchValue({
            'keywordGroups': [new MonitoringKeywordGroupModel()]
        });
        this.keywordGroups.disable();
        this.expertKeywords.enable();
        this.expertBooleanSearchEnabled = true;
        this.searchModelButtonLabel = 'Monitoring.keyWordGroup.enableStandardMode';
        this.search.searchCriteria.query.searchMode = SearchMode.expert;
    }

    /**
     * Handles changes to selected search type tab
     */
    tabChange(tabIndex: SearchTabsEnum) {
        this.selectedTabIndex = tabIndex;

        switch(tabIndex) {
            case SearchTabsEnum.newCoverage: {
                this.search.searchCriteria.isAccountSearch = false;
                this.searchForm.patchValue({
                    'dateQuery': DateQueryModel.getLast24HoursQuery(),
                    'mediaTypes': this.mediaTypes.value.filter(x => !x.isSavedSearch)
                });
                this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
                break;
            }
            case SearchTabsEnum.savedCoverage: {
                this.search.searchCriteria.isAccountSearch = true;
                this.searchForm.patchValue({
                    'dateQuery': DateQueryModel.getAllQuery()
                });
                this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
                break;
            }
        }
    }

    /**
     * Adds a new keyword group to keywordGorups FormArray & collapses existing keywordGroups
    */
    addKeywordGroup() {
        this.keywordGroupList.forEach(group => group.containerExpanded = false);
        this.keywordGroups.push(new FormControl(new MonitoringKeywordGroupModel()));
    }

    /**
     * Deletes keyword group from FormArray
    */
    deleteKeywordGroup(index: number) {
        this.keywordGroups.removeAt(index);
    }

    private getFolderTagValues(
        multiselect: MultiselectAutocompleteModel<Folder|TagModel>[],
        type: SeriesTypeEnum
    ): number[] {
        const value = multiselect.find(x => x.type === type);
        return (value ? value.value.map(x => x.id) : []);
    }

    private setFolderTagValues(includeOrExclude: IncludeOrExclude): MultiselectAutocompleteModel<Folder|TagModel>[] {
        const folderIds = this.search.searchCriteria.query.folder[includeOrExclude];
        const tagIds = this.search.searchCriteria.query.tag[includeOrExclude];


        const folderAutocomplete = this.folderMultiselectAutocompleteModel.clone();
        folderAutocomplete.value = folderIds.map(id => this.folders.find(x => x.id === id));

        const tagAutocomplete = this.tagMulitselectAutocompleteModel.clone();
        tagAutocomplete.value = tagIds.map(id => this.tags.find(x => x.id === id));
        return [folderAutocomplete, tagAutocomplete];
    }


    private determineIfImageSearch(formValues?) {
        const keywordGroups = formValues?.requiredSource.keywords.keywordGroups || [new MonitoringKeywordGroupModel()];
        const expertBooleanQuery = formValues?.requiredSource.keywords.expertBooleanQuery || new MonitoringAdvancedBooleanModel();
        const isImageSearchInKeywordGroups = keywordGroups.some(
            (group) => group.searchContexts.some(x => x.value === SearchContextsEnum.images)
        );
        const isImageSearchInExpertQuery = expertBooleanQuery.searchContexts &&
            expertBooleanQuery.searchContexts.some(x => x.value === SearchContextsEnum.images);

        this.isImageSearch = isImageSearchInKeywordGroups || isImageSearchInExpertQuery;
    }

    /**
     * Open dialog to let user create backfill request
     */
    public backfill(): void {
        this.coreDialogService.open(TopicBackfillDialogComponent, {
            inputs: {
                topic: this.search
            },
            outputs: {
                dialogClose: (requestSent: boolean) => {
                    if (requestSent) {
                        this.refreshTopics();
                    }
                }
            }
        });
    }

    public reloadBackfills(): void {
        if (!this.search?.id) {
            return;
        }
        this.topicBackfillApi.getAllWithCount(this.search.id).subscribe((backfills) => {
            this.backfills = backfills.map(backfillApiModel => {
                return new TopicBackfillModel(backfillApiModel);
            });
            this.getBackfillAttributes();
        });
    }

    public getBackfillAttributes(): void {

        if (!this.search || !this.backfills) {
            this.backfillAttributes = [];
            return;
        }
        const backfills = this.backfills.slice();
        backfills.sort((a, b) => +b.createdDate - +a.createdDate);
        this.backfillAttributes = backfills.map(
            (backfill, index) => {
                return [
                    {
                        id: `backfill.${index}.requestedDate`,
                        label: 'Monitoring.search.backfill.dateRequested',
                        value: backfill.createdDate,
                        type: attributeTypesEnum.date,
                        icon: backfill.totalMentions ? 'fa-regular fa-duotone fa-circle-check' : 'fa-kit fa-agpr-in-progress',
                        iconToolTip:  backfill.totalMentions
                            ? 'Monitoring.search.backfill.status.completed'
                            : 'Monitoring.search.backfill.status.inProgress'
                    },
                    {
                        id: `backfill.${index}.fromDate`,
                        label: 'Monitoring.search.backfill.fromDate',
                        value: backfill.dateFilter.dateRange.start,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.toDate`,
                        label: 'Monitoring.search.backfill.toDate',
                        value: backfill.dateFilter.dateRange.end,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.mentionCount`,
                        label: 'Mentions',
                        value: backfill.totalMentions,
                        type: attributeTypesEnum.number
                    }
                ];
            }
        );
    }

    /**
     * Refreshes to display the current details and statuses
     */
    refreshTopics(): void {
        if (this.search) {
            this.topicsService.updateTopic(this.search);
        }
    }

    openSaveAsGroupDialog(includeOrExclude: IncludeOrExclude) {
        let sources = includeOrExclude === IncludeOrExclude.include ?
            this.search.searchCriteria.query.includedSources :
            this.search.searchCriteria.query.excludedSources;
        this.coreDialogService.open(SaveAsGroupDialogComponent, {
            inputs: {
                sourceList: sources
            },
            outputs: {
                dialogClose: (result: {group: SourceGroup; includedSources: JournodexSourceModel[]}) => {
                    if (result) {
                        // remove sources & groups added to group
                        sources = sources.filter(source => !result.includedSources.includes(source));
                        // add new group to sources
                        if (!sources.find(source => source.entityName === result.group.id.toString())) {
                            const group: JournodexSourceModel = new JournodexSourceModel();
                            group.entityType = SearchCriteriaSourcesEnum.sourceGroups;
                            group.entityName = result.group.id.toString();
                            group.displayString = result.group.name;
                            sources.push(group);
                        }

                        if (includeOrExclude === IncludeOrExclude.include) {
                            this.searchForm.patchValue({
                                'requiredSource': {
                                    'sources': {
                                        'includedSources': sources.slice()
                                    }
                                }
                            });
                        } else {
                            this.searchForm.patchValue({
                                'requiredSource': {
                                    'sources': {
                                        'excludedSources': sources.slice()
                                    }
                                }
                            });
                        }
                    }
                }
            }
        });
    }

    openDiscoverUpsellDialog()  {
        if (this.isPaMedia) {
            window.location.href = 'mailto:mediapointsupport@pressassociation.com';
        } else {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );

            const io = {
                inputs: {
                    animationTrigger: animationTrigger,
                    overlayContainer: this.overlayContainer,
                    type: UpsellDialogType.discover
                }
            };
            this.coreDialogService.open(UpsellInfoDialogComponent, io);
        }
    }

    // Create a targeting search and set to selected discover list to be applied as a filter
    setDiscoverListFilterSearchId(): Observable<MentionSearchModel> {
        if (!this.discoverList.value?.currentFilterSearchContext || !this.isDiscoverListFilterDirty) {
            return of(this.search);
        }
        const discoverListFilter = this.discoverList.value as DiscoverListFilterModel;
        discoverListFilter.currentFilterSearchContext.isFilterSearch = true;

        return this.targetingSearchesApiService.create(discoverListFilter.currentFilterSearchContext).pipe(
            switchMap(((id) =>{
                const value = this.discoverList.value.clone();
                value.filterSearchId = id;
                this.discoverList.patchValue(value);
                this.search.searchCriteria.query.discoverListFilter.filterSearchId = id;
                return of(this.search);
            })));
    }

    showContextTakeover(naturalLanguagePrompt: string): void {
        const createStages: ProgressStageModel[] = [
            {
                messages: [
                    'Monitoring.search.prcopilot.stage.analyzingPurpose',
                    'Monitoring.search.prcopilot.stage.searchingInternet',
                    'Monitoring.search.prcopilot.stage.identifyingKeywords',
                ],
                approximateDurationMsPerMessage: 5500,
            },
            {
                messages: ['Monitoring.search.prcopilot.stage.creatingQuery'],
                approximateDurationMsPerMessage: 7000,
            },
            {
                messages: [
                    'Monitoring.search.prcopilot.stage.reviewingResults',
                    'Monitoring.search.prcopilot.stage.refiningQuery',
                ],
                mayLoop: true,
                approximateDurationMsPerMessage: 6000,
            },
            {
                messages: ['Monitoring.search.prcopilot.stage.preparingFinalQuery'],
                approximateDurationMsPerMessage: 7000,
            },
        ];
        this.topicsService.openTopicAgentDialog(
            naturalLanguagePrompt,
            this.taskDescription,
            this.systemPrompt || null,
            this.topicSystemPrompt || null,
            (topic) => this.topicsService.navigateToNewSearchWithTopic(topic),
            (refined) => { this.naturalLanguagePrompt = refined; },
            createStages
        );
    }

    refineExistingTopic(prompt: string): void {
        if (this.isRefining) {
            return;
        }
        this.isRefining = true;

        const refineStages: ProgressStageModel[] = [
            {
                messages: [
                    'Monitoring.search.prcopilot.stage.analyzingPurpose',
                    'Monitoring.search.prcopilot.stage.searchingInternet',
                    'Monitoring.search.prcopilot.stage.identifyingKeywords',
                ],
                approximateDurationMsPerMessage: 5500,
            },
            {
                messages: ['Monitoring.search.prcopilot.stage.creatingQuery'],
                approximateDurationMsPerMessage: 7000,
            },
            {
                messages: [
                    'Monitoring.search.prcopilot.stage.reviewingResults',
                    'Monitoring.search.prcopilot.stage.refiningQuery',
                ],
                mayLoop: true,
                approximateDurationMsPerMessage: 6000,
            },
            {
                messages: ['Monitoring.search.prcopilot.stage.preparingFinalQuery'],
                approximateDurationMsPerMessage: 7000,
            },
        ];
        this.coreDialogService.open(ProgressDialogComponent, {
            inputs: {
                id: 'monitoringSearchRefine',
                alertMessage: 'Monitoring.search.prcopilot.generatingMessage',
                alertType: messageSeverityEnum.info,
                headerLabel: 'Monitoring.search.prcopilot.editTitle',
                showCount: false,
                work$: this.topicsService.refineTopicWithAgent(this.search, prompt),
                estimatedDurationMs: 40000,
                progressStages: refineStages,
            },
            outputs: {
                dialogClose: (completed: boolean) => {
                    if (completed) {
                        this.naturalLanguagePrompt = '';
                    }
                    this.isRefining = false;
                }
            }
        });
    }

    showRefineContextTakeover(prompt: string): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        this.coreDialogService.open(AIContextTakeoverComponent, {
            inputs: {
                title: 'Monitoring.search.agilityAgent.contextTakeoverTitle',
                originalPurposeLabel: 'Monitoring.search.prcopilot.originalPurpose',
                refinedPurposeLabel: 'Monitoring.search.prcopilot.refinedPurpose',
                naturalLanguagePrompt: prompt,
                taskDescription: this.taskDescription,
                systemPrompt: this.systemPrompt || null,
                overlayContainer: document.getElementById('main-content'),
                animationTrigger,
                applyFn: (refinedPurpose: string) => this.agentsApiService.updateTopic(this.search.toApiModel(), refinedPurpose).pipe(
                    tap(response => {
                        const updatedSearch = this.search.clone(false);
                        updatedSearch.searchCriteria.query = new MentionSearchQueryModel(response.topic.query);
                        if (response.topic.searchIntent) {
                            updatedSearch.searchIntent = response.topic.searchIntent;
                        }
                        this.populateFormFromTopic(updatedSearch);
                        this.naturalLanguagePrompt = '';
                    })
                )
            },
            outputs: {
                dialogClose: (refinedPurpose: string) => {
                    if (refinedPurpose) {
                        this.naturalLanguagePrompt = refinedPurpose;
                    }
                }
            }
        });
    }

    private populateFormFromTopic(topic: MentionSearchModel, mediaTypes?: MediaTypeModel[]): void {
        this.search = topic;
        const query = this.search.searchCriteria.query;

        // Determine if this is an image search BEFORE populating form values
        // Pass the query's keyword groups so it can evaluate properly
        this.determineIfImageSearch({
            requiredSource: {
                keywords: {
                    keywordGroups: query.keywordGroups,
                    expertBooleanQuery: query.expertBooleanQuery,
                }
            }
        });

        if (!mediaTypes) {
            this.addTvSubTypes(query.includedMediaTypes, query.includedTvTypes);
        }

        this.searchForm.patchValue({
            requiredSource: {
                keywords: {
                    keywordGroups: [],
                    expertBooleanQuery: query.expertBooleanQuery,
                },
                folderTag: {
                    folderTagInclude: this.setFolderTagValues(IncludeOrExclude.include),
                    folderTagExclude: this.setFolderTagValues(IncludeOrExclude.exclude),
                },
                sources: {
                    includedSources: query.includedSources,
                    excludedSources: query.excludedSources,
                },
                discoverListFilter: query.discoverListFilter
            },
            name: this.search.name,
            mediaTypes: mediaTypes ?? query.includedMediaTypes,
            regions: {
                includedRegions: query.includedRegions,
                excludedRegions: query.excludedRegions,
            },
            languages: {
                includedLanguages: query.includedLanguages,
                excludedLanguages: query.excludedLanguages,
            },
            sentiments: query.sentiments,
            caseSensitive: query.caseSensitive,
            // hide-similar is a runtime filter, not persisted on the topic — reflect the live duplicates
            // filter (toolbar) so the checkbox stays in sync when the filter is changed/removed there.
            hideSimilar: this.mentionSearchService.duplicates.getValue() === DuplicatesStatusEnum.hide,
            dateQuery: query.dateQuery,
            searchIntent: topic.searchIntent || '',
        });

        this.keywordGroups.clear();
        query.keywordGroups.forEach(value => {
            this.keywordGroups.push(new FormControl(value, [this.keywordValidator()]));
        });
        this.hasEmptyKeywordGroup = this.keywordGroups.value.some(group => !group.isAnyKeywordSet());

        if (this.search.hasName()) {
            this.name.enable();
        } else {
            this.name.disable();
        }

        this.showErrors = false;

        // Minimum 2 sources, with at least one that is not a source group
        this.showSaveIncludeSources = query.includedSources.length >= 1
            && query.includedSources.filter(source => source.entityType !== SourceTypeEnum.sourceGroups).length >= 1;

        this.showSaveExcludeSources = query.excludedSources.length >= 1
            && query.excludedSources.filter(source => source.entityType !== SourceTypeEnum.sourceGroups).length >= 1;

        this.expertBooleanSearchEnabled = query.isExpertSearch();
        if (this.expertBooleanSearchEnabled) {
            this.searchModelButtonLabel = 'Monitoring.keyWordGroup.enableStandardMode';
            this.expertKeywords.enable();
        }
    }

    runTopicAgentDirect(): void {
        this.agentsApiService.createTopic(this.naturalLanguagePrompt, this.topicSystemPrompt || null)
            .pipe(
                switchMap(response => this.topicsService.navigateToNewSearchWithTopic(response.topic as unknown as SavedMentionSearchApiModel))
            ).subscribe();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
